/**
 * TypeScript types mirroring the Java DTOs in
 * `libs/domain-patient/src/main/java/com/addy/his/patient/api/dto/`.
 *
 * Hand-maintained until openapi-typescript is wired (M24). Keep in sync with
 * the Java side — adding a field to the Java DTO must add it here too.
 */

export type PatientGender = "M" | "F" | "OTHER" | "UNKNOWN";

export type PatientStatus = "ACTIVE" | "ARCHIVED" | "MERGED" | "DECEASED";

export type DobPrecision = "DAY" | "MONTH" | "YEAR";

export type BloodType =
  | "A_POS" | "A_NEG"
  | "B_POS" | "B_NEG"
  | "AB_POS" | "AB_NEG"
  | "O_POS" | "O_NEG"
  | "UNKNOWN";

export type GuardianRelationship =
  | "MOTHER" | "FATHER" | "SPOUSE" | "CHILD" | "SIBLING" | "OTHER";

export type IdentifierType =
  | "CCCD" | "CMND" | "BHYT" | "PASSPORT" | "PHONE" | "GUARDIAN";

export interface PatientIdentifierResponse {
  id: string;
  identifierType: IdentifierType;
  value: string;
  issuedBy: string | null;
  issuedAt: string | null; // ISO yyyy-MM-dd
  isPrimary: boolean;
  createdAt: string;
}

export type AllergyCategory = "DRUG" | "FOOD" | "ENVIRONMENT" | "OTHER";
export type AllergySeverity = "MILD" | "MODERATE" | "SEVERE" | "ANAPHYLAXIS";
export type AllergySource = "PATIENT_REPORTED" | "CLINICIAN" | "IMPORTED";

export type AlertType =
  | "ALLERGY" | "DRUG_INTERACTION" | "PREGNANCY"
  | "HIGH_RISK" | "OVERDUE_FOLLOWUP" | "CUSTOM";
export type AlertSeverity = "INFO" | "WARNING" | "CRITICAL";

export type ConditionSeverity = "MILD" | "MODERATE" | "SEVERE";

export type DuplicateConfidence = "STRONG" | "LIKELY" | "WEAK";

export interface PatientCreateRequest {
  fullName: string;
  gender?: PatientGender | null;
  dateOfBirth?: string | null;     // ISO yyyy-MM-dd
  dobPrecision?: DobPrecision | null;
  phone?: string | null;
  email?: string | null;
  addressLine?: string | null;
  wardCode?: string | null;
  provinceCode?: string | null;
  nationality?: string | null;
  ethnicityCode?: string | null;
  occupation?: string | null;
  organization?: string | null;
  bloodType?: BloodType | null;
  heightCm?: number | null;
  weightKg?: number | null;
  notes?: string | null;
  guardianName?: string | null;
  guardianPhone?: string | null;
  guardianRelationship?: GuardianRelationship | null;
  primaryBranchId?: string | null;
  primaryDoctorId?: string | null;
  cccd?: string | null;
  cmnd?: string | null;
  bhyt?: string | null;
  forceDuplicate?: boolean;
}

export type PatientUpdateRequest = Omit<
  PatientCreateRequest,
  "cccd" | "cmnd" | "bhyt" | "forceDuplicate"
>;

export interface PatientResponse {
  id: string;
  patientCode: string | null;
  fullName: string;
  gender: PatientGender | null;
  dateOfBirth: string | null;
  dobPrecision: DobPrecision;
  age: number | null;
  phoneE164: string | null;
  email: string | null;
  addressLine: string | null;
  wardCode: string | null;
  provinceCode: string | null;
  nationality: string;
  ethnicityCode: string | null;
  occupation: string | null;
  organization: string | null;
  bloodType: BloodType | null;
  heightCm: number | null;
  weightKg: number | null;
  notes: string | null;
  guardianName: string | null;
  guardianPhone: string | null;
  guardianRelationship: GuardianRelationship | null;
  primaryBranchId: string | null;
  primaryDoctorId: string | null;
  status: PatientStatus;
  mergedIntoId: string | null;
  deceasedAt: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
  /** Ảnh Zalo (Mini App) HIS đã lưu; API bỏ trường khi không có ảnh. */
  zaloAvatarUrl?: string | null;
  /** Ảnh đại diện HIS lưu (URL presigned, hết hạn sau vài phút); API bỏ trường khi không có. */
  avatarUrl?: string | null;
}

export interface PatientSummaryResponse {
  id: string;
  patientCode: string | null;
  fullName: string;
  gender: PatientGender | null;
  dateOfBirth: string | null;
  age: number | null;
  phoneE164: string | null;
  email: string | null;
  addressLine: string | null;
  provinceCode: string | null;
  wardCode: string | null;
  guardianName: string | null;
  status: PatientStatus;
  updatedAt: string;
  /** Ảnh Zalo (Mini App) HIS đã lưu; API bỏ trường khi không có ảnh. */
  zaloAvatarUrl?: string | null;
  /** Ảnh đại diện HIS lưu (URL presigned, hết hạn sau vài phút); API bỏ trường khi không có. */
  avatarUrl?: string | null;
}

export interface PatientSearchResult extends PatientSummaryResponse {
  matchScore?: number | null;
}

export interface PatientDuplicateCandidate {
  patientId: string;
  patientCode: string | null;
  fullName: string;
  phoneE164: string | null;
  dateOfBirth: string | null;
  reason: string;
}

export interface PatientDuplicateWarning {
  confidence: DuplicateConfidence;
  score: number;
  candidates: PatientDuplicateCandidate[];
  reasons: string[];
}

export interface PatientCreateResponse {
  patient: PatientResponse;
  warning: PatientDuplicateWarning | null;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface PatientListFilter {
  q?: string | null;
  status?: PatientStatus | null;
  gender?: PatientGender | null;
  provinceCode?: string | null;
  primaryBranchId?: string | null;
  primaryDoctorId?: string | null;
  ageMin?: number | null;
  ageMax?: number | null;
}

// ---- Allergy ----
export interface AllergyCreateRequest {
  category: AllergyCategory;
  substance: string;
  substanceCode?: string | null;
  severity: AllergySeverity;
  reaction?: string | null;
  onsetDate?: string | null;
  source?: AllergySource;
}
export interface AllergyUpdateRequest {
  category: AllergyCategory;
  substance: string;
  substanceCode?: string | null;
  severity: AllergySeverity;
  reaction?: string | null;
  onsetDate?: string | null;
}
/** One allergy a picked medicine matched (#18859). */
export interface DrugAllergyWarning {
  allergyId: string;
  substance: string;
  severity: AllergySeverity;
  reaction: string | null;
  /** CODE | ACTIVE_INGREDIENT | NAME — why it matched, so the doctor can judge it. */
  matchedOn: "CODE" | "ACTIVE_INGREDIENT" | "NAME";
  /** SEVERE or ANAPHYLAXIS — the two tiers that warrant the strongest presentation. */
  severe: boolean;
  /**
   * The doctor already answered this exact warning on this visit (#18859 §9), so it is not raised
   * again. It never means the warning did not fire — the override is on the record either way.
   */
  overridden: boolean;
}

/**
 * Why a doctor prescribed through a drug-allergy warning (#18859 §5). A reason is required; the
 * fixed codes make overrides countable, and OTHER keeps the escape hatch at the cost of words.
 */
/** One medicine on a visit's prescription that clashes with an active drug allergy (#19184). */
export interface EncounterAllergyConflict {
  medicineId: string;
  medicineName: string;
  allergyId: string;
  substance: string;
  severity: AllergySeverity;
  reaction: string | null;
  matchedOn: "CODE" | "ACTIVE_INGREDIENT" | "NAME";
  severe: boolean;
  /** Already answered for this visit (on the visit or on the Đơn thuốc screen). */
  overridden: boolean;
}

export type AllergyOverrideReason =
  | "ALLERGY_RECORD_INACCURATE"
  | "PREVIOUSLY_TOLERATED"
  | "BENEFIT_OUTWEIGHS_RISK"
  | "OTHER";

export const allergyOverrideReasonLabel: Record<AllergyOverrideReason, string> = {
  ALLERGY_RECORD_INACCURATE: "Ghi nhận dị ứng chưa chính xác",
  PREVIOUSLY_TOLERATED: "Bệnh nhân đã dùng thuốc này và dung nạp được",
  BENEFIT_OUTWEIGHS_RISK: "Lợi ích điều trị vượt trội nguy cơ",
  OTHER: "Lý do khác",
};

export const ALLERGY_OVERRIDE_REASONS: AllergyOverrideReason[] = [
  "ALLERGY_RECORD_INACCURATE",
  "PREVIOUSLY_TOLERATED",
  "BENEFIT_OUTWEIGHS_RISK",
  "OTHER",
];

export interface AllergyResponse {
  id: string;
  category: AllergyCategory;
  substance: string;
  substanceCode: string | null;
  severity: AllergySeverity;
  reaction: string | null;
  onsetDate: string | null;
  verifiedAt: string | null;
  verifiedBy: string | null;
  source: AllergySource;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

// ---- Chronic condition ----
export interface ConditionCreateRequest {
  icd10Code?: string | null;
  nameVi: string;
  onsetDate?: string | null;
  severity?: ConditionSeverity | null;
  notes?: string | null;
}
export interface ConditionUpdateRequest extends ConditionCreateRequest {
  resolvedDate?: string | null;
}
export interface ConditionResponse {
  id: string;
  icd10Code: string | null;
  nameVi: string;
  onsetDate: string | null;
  resolvedDate: string | null;
  severity: ConditionSeverity | null;
  notes: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

// ---- Alert ----
export interface AlertCreateRequest {
  alertType: AlertType;
  severity: AlertSeverity;
  titleVi: string;
  descriptionVi?: string | null;
  expiresAt?: string | null;
}
export interface AlertResponse {
  id: string;
  alertType: AlertType;
  severity: AlertSeverity;
  titleVi: string;
  descriptionVi: string | null;
  expiresAt: string | null;
  acknowledgedBy: string | null;
  acknowledgedAt: string | null;
  active: boolean;
  createdAt: string;
}

// ---- Tags ----
export interface TagsResponse {
  tags: string[];
}
